import { useCallback, useRef, useState } from 'react';

export function useLastPage({
  totalItems,
  itemsPerPage,
}: {
  totalItems: number;
  itemsPerPage: number;
}) {
  const lastPage = Math.ceil(totalItems / itemsPerPage);
  const lastPageRef = useRef(0);

  if (lastPage !== 0) {
    lastPageRef.current = lastPage;
  }

  if (totalItems === 0) {
    lastPageRef.current = 1;
  }

  return {
    lastPage: lastPageRef.current,
  };
}

export function usePagination({
  pageSize,
  initialPage,
  onPageChange,
}: {
  /** Page size */
  pageSize: number;
  /** Initial page number. */
  initialPage?: number;
  /** On page change callback function */
  onPageChange?: (page: number) => void;
}) {
  const [currentPage, setCurrentPage] = useState(initialPage ?? 1);
  const [offset, setOffset] = useState(initialPage ? (initialPage - 1) * pageSize : 0);

  const handleOnPageChange = useCallback(
    (page: number) => {
      setOffset((page - 1) * pageSize);
      setCurrentPage(page);
      onPageChange?.(page);
    },
    [pageSize, onPageChange],
  );

  const pageSections = {
    first: [currentPage, currentPage + 1, currentPage + 2],
    middle: [currentPage - 1, currentPage, currentPage + 1],
    last: [currentPage - 2, currentPage - 1, currentPage],
  };

  const pageSection = (lastPage: number) =>
    currentPage === 1
      ? pageSections['first']
      : currentPage === lastPage
        ? pageSections['last']
        : pageSections['middle'];

  return {
    currentPage,
    setCurrentPage,
    offset,
    setOffset,
    onPageChange: handleOnPageChange,
    onFirst: () => handleOnPageChange(1),
    onLast: (lastPage: number) => handleOnPageChange(lastPage),
    onPrevious: () => handleOnPageChange(currentPage - 1),
    onNext: () => handleOnPageChange(currentPage + 1),
    pageSection,
  };
}
